import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { UserRole } from '../types';
import pRetry from 'p-retry';
import { analytics } from './analytics-events';

interface AuthContextType {
  user: User | null;
  userRole: UserRole;
  loading: boolean;
  signIn: (email: string, password: string, recaptchaToken: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole>('viewer');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const role = session.user.user_metadata?.role || 'viewer';
        setUserRole(role as UserRole);
        console.log('User session loaded:', { 
          id: session.user.id,
          role: role,
          metadata: session.user.user_metadata 
        });
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const role = session.user.user_metadata?.role || 'viewer';
        setUserRole(role as UserRole);
        console.log('Auth state changed:', { 
          id: session.user.id,
          role: role,
          metadata: session.user.user_metadata 
        });
      } else {
        setUserRole('viewer');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string, recaptchaToken: string) => {
    try {
      if (!recaptchaToken) {
        console.error('No reCAPTCHA token provided');
        return { error: 'Security verification failed. Please try again.' };
      }

      // Verify reCAPTCHA token with retry
      const verifyResponse = await pRetry(
        async () => {
          const response = await fetch('https://hook.us1.make.com/yuq3514axh9432t2ki64pjynh76d0llz', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'login',
              token: recaptchaToken,
              email
            })
          });

          if (!response.ok) {
            throw new Error('reCAPTCHA verification failed');
          }

          return response;
        },
        {
          retries: 3,
          factor: 2,
          minTimeout: 1000,
          onFailedAttempt: error => {
            console.log(
              `reCAPTCHA verification attempt ${error.attemptNumber} failed. ${error.retriesLeft} retries left.`
            );
          }
        }
      );

      if (!verifyResponse.ok) {
        console.error('reCAPTCHA verification failed:', await verifyResponse.text());
        return { error: 'Security verification failed. Please try again.' };
      }

      // Sign in with Supabase with retry
      const authResult = await pRetry(
        async () => {
          const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) {
            throw new Error(error.message);
          }

          return { error: null };
        },
        {
          retries: 3,
          factor: 2,
          minTimeout: 1000,
          onFailedAttempt: error => {
            console.log(
              `Sign in attempt ${error.attemptNumber} failed. ${error.retriesLeft} retries left.`
            );
          }
        }
      );

      if (authResult.error === null) {
        analytics.trackLogin('email');
      }

      return authResult;
    } catch (err) {
      console.error('Sign in error:', err);
      analytics.trackError(
        err instanceof Error ? err.message : 'Failed to sign in',
        'AuthContext.signIn'
      );
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
      return {
        error: `${errorMessage}. Please check your connection and try again.`
      };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: 'viewer'
          }
        }
      });

      if (!error) {
        analytics.trackSignUp('email');
      }

      return { error: error?.message ?? null };
    } catch (err) {
      console.error('Sign up error:', err);
      analytics.trackError(
        err instanceof Error ? err.message : 'Failed to create account',
        'AuthContext.signUp'
      );
      return { error: 'Failed to create account. Please try again later.' };
    }
  };

  const signOut = async () => {
    analytics.trackLogout();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, userRole, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
